import React from "react";
import { Link } from "@inertiajs/react";
import { Box, Image, Stack, Group } from "@mantine/core";
import { IconCalendar, IconMapPin, IconCamera } from "@tabler/icons-react";
import { Badge } from "./UI/Badge";
import { Heading3, DimmedText } from "./UI/Typography";
import type { GalleryGig } from "../data/gallery";

interface GigCardProps {
  gig: GalleryGig;
}

const GigCard: React.FC<GigCardProps> = ({ gig }) => {
  const [hovered, setHovered] = React.useState(false);

  const formattedDate = new Date(gig.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Link href={`/media/${gig.slug}`} style={{ textDecoration: "none", display: "block" }}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          borderRadius: "var(--mantine-radius-md)",
          overflow: "hidden",
          backgroundColor: "rgba(18,18,18,0.55)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: hovered ? "translateY(-6px)" : "translateY(0)",
          boxShadow: hovered
            ? "0 24px 64px rgba(212,85,43,0.18), 0 0 0 1px rgba(212,85,43,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "inset 0 1px 0 rgba(255,255,255,0.07), 0 8px 32px rgba(0,0,0,0.4)",
          border: hovered
            ? "1px solid rgba(212,85,43,0.22)"
            : "1px solid rgba(212,85,43,0.12)",
          cursor: "pointer",
        }}
      >
        <Box style={{ aspectRatio: "16/10", overflow: "hidden", position: "relative" }}>
          <Image
            src={gig.coverImage}
            alt={gig.title}
            fit="cover"
            style={{
              width: "100%",
              height: "100%",
              transition: "transform 0.5s ease",
              transform: hovered ? "scale(1.08)" : "scale(1)",
            }}
          />

          {/* Dark-to-transparent gradient from bottom */}
          <Box
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgba(18,18,18,0.9) 0%, rgba(18,18,18,0.1) 60%, transparent 100%)",
            }}
          />

          {/* Photo count chip */}
          <Group
            gap="xs"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              background: "rgba(18,18,18,0.7)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderRadius: "var(--mantine-radius-sm)",
              padding: "4px 10px",
              border: "1px solid rgba(212,85,43,0.2)",
            }}
          >
            <IconCamera size={13} style={{ color: "#e85d3c" }} />
            <DimmedText size="xs" style={{ color: "rgba(255,255,255,0.9)" }}>
              {gig.photos.length} photos
            </DimmedText>
          </Group>

          {/* Tags on image bottom */}
          <Group gap="xs" style={{ position: "absolute", bottom: 12, left: 12 }}>
            {gig.tags.slice(0, 2).map((tag) => (
              <Badge
                key={tag}
                size="sm"
                radius="sm"
                style={{
                  background: "rgba(18,18,18,0.7)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  border: "1px solid rgba(212,85,43,0.2)",
                  color: "white",
                }}
              >
                {tag}
              </Badge>
            ))}
          </Group>
        </Box>

        <Stack gap="xs" p="lg">
          <Heading3 style={{ lineHeight: 1.3 }}>{gig.title}</Heading3>

          <Group gap="lg">
            <Group gap="xs">
              <IconCalendar size={13} style={{ color: "rgba(212,85,43,0.5)" }} />
              <DimmedText size="xs">{formattedDate}</DimmedText>
            </Group>
            {gig.location && (
              <Group gap="xs">
                <IconMapPin size={13} style={{ color: "rgba(212,85,43,0.5)" }} />
                <DimmedText size="xs">{gig.location}</DimmedText>
              </Group>
            )}
          </Group>

          {gig.description && (
            <DimmedText
              size="sm"
              style={{
                lineHeight: 1.6,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {gig.description}
            </DimmedText>
          )}
        </Stack>
      </Box>
    </Link>
  );
};

export default GigCard;
